// src/components/common/SelfFilterPopover.tsx
//
// Self-managed counterpart of RaFilterPopover. For screens whose filter STATE
// lives in their own React state (not in a React-Admin <List>), e.g. each Logs
// tab. It renders the given filter fields inside the shared SectionHeader popover
// and reports the active-filter count + refresh action to the same
// FilterPopoverProvider/FilterPopoverContext used by the RA bridge.
//
// Usage (inside a child rendered under <FilterPopoverProvider>):
//   <SelfFilterPopover count={activeCount} onRefresh={fetchLogs} refreshing={loading}>
//     <TextField ... />
//     <Select ... />
//   </SelfFilterPopover>
//
// The parent (page) renders <SectionHeader useRaFilters left={tabs} /> which reads
// the same context to drive the Filters button (anchor/open/badge) and the Refresh.

import React, { useEffect } from 'react';
import { Box, Popover, useTheme } from '@mui/material';
import { useFilterPopover } from './RaFilterPopover';

interface SelfFilterPopoverProps {
  /** Filter input elements (fully controlled by the parent's own state). */
  children?: React.ReactNode;
  /** Number of active filters — drives the SectionHeader badge. */
  count?: number;
  /** Refresh/apply action, surfaced in the SectionHeader right cluster. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Max width of the popover. */
  maxWidth?: number;
}

export const SelfFilterPopover: React.FC<SelfFilterPopoverProps> = ({
  children,
  count = 0,
  onRefresh,
  refreshing,
  maxWidth = 680,
}) => {
  const theme = useTheme();
  const ctx = useFilterPopover();

  const hasChildren = React.Children.toArray(children).length > 0;

  // IMPORTANT: these effects must depend on the STABLE context setters, never on
  // the context value object itself. The provider recreates its value whenever
  // any piece of state changes, so depending on `ctx` re-runs the effect (and its
  // cleanup) on every provider render; the cleanup+setup pair then writes a new
  // `refresh` object into the provider, which recreates the value again — an
  // endless setState loop that saturates the main thread and blocks navigation.
  const setHasFilters = ctx?.setHasFilters;
  const setActiveCount = ctx?.setActiveCount;
  const setRefresh = ctx?.setRefresh;

  // Tell the header a Filters button should appear while this content is mounted.
  useEffect(() => {
    setHasFilters?.(hasChildren);
    return () => setHasFilters?.(false);
  }, [setHasFilters, hasChildren]);

  // Report the active-filter count (parent computes it).
  useEffect(() => {
    setActiveCount?.(count);
  }, [setActiveCount, count]);

  // Register the refresh action so the header's Refresh button drives this tab.
  useEffect(() => {
    setRefresh?.({ onRefresh, refreshing });
    return () => setRefresh?.({});
  }, [setRefresh, onRefresh, refreshing]);

  if (!ctx || !hasChildren) return null;

  return (
    <Popover
      open={ctx.open}
      anchorEl={ctx.anchorEl}
      onClose={() => ctx.setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            p: 2,
            borderRadius: theme.custom?.radius?.card ?? '10px',
            maxWidth,
            boxShadow: theme.custom?.shadows?.medium,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          '& .MuiInputBase-root': {
            minHeight: '36px !important',
            height: 36,
            fontSize: '0.8125rem',
            backgroundColor: 'background.paper',
            borderRadius: theme.custom?.radius?.small ?? '8px',
          },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          '& .MuiFormControl-root': { m: 0 },
          '& .MuiFormHelperText-root': { display: 'none' },
        }}
      >
        {children}
      </Box>
    </Popover>
  );
};

export default SelfFilterPopover;
